import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import IconButton from 'core/elements/button/IconButton'
import Dropdown from 'core/elements/dropdown'
import Input from 'core/elements/input'
import Button from 'core/elements/button'
import ServerSideFilters, { FilterConfig } from './ServerSideFilters'
import ServerSideSort, { SortFieldOption } from './ServerSideSort'

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'grid',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },
  firstCol: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  secondCol: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },
  searchSection: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  searchDropdown: {
    minWidth: 150,
    '& ul': {
      left: '-1px',
    },
  },
  searchInput: {
    minWidth: 200,
    flex: 1,
    gap: 0,
  },
  iconButton: {
    width: 38,
    height: 38,
  },
  clearButton: {
    height: 38,
  },
  secondRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    justifyContent: 'flex-start',
  },
}))

export interface SearchFieldOption {
  value: string
  label: string
}

export interface ServerSideSearchParams {
  searchField: string
  searchValue: string
  filters: Record<string, string>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

export interface ServerSideSearchProps {
  /** Available search field options */
  searchFields: SearchFieldOption[]
  /** Filter configurations (optional) */
  filterConfigs?: FilterConfig[]
  /** Sort field options (optional) */
  sortOptions?: SortFieldOption[]
  /** Callback when search/filter/sort is triggered */
  onSearch: (params: ServerSideSearchParams) => void
  /** Callback when clear all is triggered */
  onClear: () => void
  /** Loading state */
  isLoading?: boolean
  /** Initial search params from session */
  initialParams?: Partial<ServerSideSearchParams>
  /** Default sort key */
  defaultSortKey?: string
  /** Default sort direction */
  defaultSortDir?: 'asc' | 'desc'
  /** Additional content to render (e.g., toggles) */
  extraContent?: React.ReactNode
}

/**
 * Generic server-side search component.
 *
 * Combines search input, filters, and sort into a unified toolbar.
 * All parameters are passed to the parent via callbacks for API calls.
 */
export const ServerSideSearch: React.FC<ServerSideSearchProps> = ({
  searchFields,
  filterConfigs = [],
  sortOptions = [],
  onSearch,
  onClear,
  isLoading = false,
  initialParams,
  defaultSortKey,
  defaultSortDir = 'desc',
  extraContent,
}) => {
  const classes = useStyles()

  // Initialize state from initial params or defaults
  const [searchField, setSearchField] = useState<string>(
    initialParams?.searchField || searchFields[0]?.value || 'name',
  )
  const [searchValue, setSearchValue] = useState<string>(initialParams?.searchValue || '')
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [sortKey, setSortKey] = useState<string>(
    initialParams?.sortKey || defaultSortKey || sortOptions[0]?.value || 'created_at',
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialParams?.sortDir || defaultSortDir)

  // Initialize filter values with defaults
  const getDefaultFilterValues = useCallback(() => {
    const defaults: Record<string, string> = {}
    filterConfigs.forEach((filter) => {
      defaults[filter.key] = filter.defaultValue || 'all'
    })
    return defaults
  }, [filterConfigs])

  const [filterValues, setFilterValues] = useState<Record<string, string>>(
    initialParams?.filters || getDefaultFilterValues(),
  )

  // Build current params object
  const buildParams = useCallback((): ServerSideSearchParams => {
    return {
      searchField,
      searchValue: searchValue.trim(),
      filters: filterValues,
      sortKey,
      sortDir,
    }
  }, [searchField, searchValue, filterValues, sortKey, sortDir])

  // Handle search button click
  const handleSearch = useCallback(() => {
    onSearch(buildParams())
  }, [buildParams, onSearch])

  // Handle filter value change
  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  // Handle sort change
  const handleSort = useCallback(
    (newSortKey: string, newSortDir: 'asc' | 'desc') => {
      setSortKey(newSortKey)
      setSortDir(newSortDir)
      // Trigger search immediately with new sort
      onSearch({
        searchField,
        searchValue: searchValue.trim(),
        filters: filterValues,
        sortKey: newSortKey,
        sortDir: newSortDir,
      })
    },
    [searchField, searchValue, filterValues, onSearch],
  )

  // Handle clear all
  const handleClearAll = useCallback(() => {
    setSearchField(searchFields[0]?.value || 'name')
    setSearchValue('')
    setFilterValues(getDefaultFilterValues())
    setSortKey(defaultSortKey || sortOptions[0]?.value || 'created_at')
    setSortDir(defaultSortDir)
    onClear()
  }, [searchFields, getDefaultFilterValues, defaultSortKey, sortOptions, defaultSortDir, onClear])

  // Handle input change with auto-clear detection
  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      const prevValue = searchValue

      // If clearing the input, trigger search with empty value
      if (prevValue.trim() !== '' && newValue.trim() === '') {
        onSearch({
          searchField,
          searchValue: '',
          filters: filterValues,
          sortKey,
          sortDir,
        })
      }

      setSearchValue(newValue)
    },
    [searchValue, searchField, filterValues, sortKey, sortDir, onSearch],
  )

  // Handle enter key
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && searchValue?.trim() !== '') {
        handleSearch()
      }
    },
    [handleSearch, searchValue],
  )

  // Check if any filter or search is active
  const hasActiveFilters = useMemo(() => {
    const hasActiveFilter = Object.entries(filterValues).some(([key, value]) => {
      const config = filterConfigs.find((f) => f.key === key)
      return value !== (config?.defaultValue || 'all')
    })
    return hasActiveFilter || searchValue.trim() !== ''
  }, [filterValues, filterConfigs, searchValue])

  const hasFilters = filterConfigs.length > 0
  const hasSort = sortOptions.length > 0

  return (
    <div className={classes.root}>
      <div className={classes.firstCol}>
        {/* Search Section */}
        <div className={classes.searchSection}>
          <Dropdown
            label="Search By"
            className={classes.searchDropdown}
            items={searchFields}
            value={searchField}
            onChange={(value) => setSearchField(value as string)}
            disabled={isLoading}
          />
          <Input
            className={classes.searchInput}
            placeholder={`Search by ${searchFields
              .find((opt) => opt.value === searchField)
              ?.label.toLowerCase()}...`}
            value={searchValue}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
          />
          <IconButton
            className={classes.iconButton}
            onClick={handleSearch}
            disabled={isLoading || !searchValue.trim()}
            size="md"
            info="Search"
            icon="search"
          />
          {hasActiveFilters ? (
            <Button
              variant="secondary"
              className={classes.clearButton}
              onClick={handleClearAll}
              disabled={isLoading}
              rightIcon="times"
              info="Clear search and filters"
            >
              Clear All
            </Button>
          ) : hasFilters ? (
            <IconButton
              className={classes.iconButton}
              onClick={() => setShowFilters(!showFilters)}
              disabled={isLoading}
              info={showFilters ? 'Hide Filters' : 'Show Additional Filters'}
              icon="filter"
            />
          ) : null}
        </div>

        <div className={classes.secondCol}>
          {extraContent}
          {hasSort && (
            <ServerSideSort
              sortOptions={sortOptions}
              onSort={handleSort}
              isLoading={isLoading}
              sortKey={sortKey}
              sortDir={sortDir}
            />
          )}
        </div>
      </div>

      {/* Filters Row */}
      {hasFilters && (showFilters || hasActiveFilters) && (
        <div className={classes.secondRow}>
          <ServerSideFilters
            filters={filterConfigs}
            values={filterValues}
            onChange={handleFilterChange}
            disabled={isLoading}
          />
          <Button onClick={handleSearch} disabled={isLoading} info="Apply Filters">
            Apply
          </Button>
        </div>
      )}
    </div>
  )
}

export default ServerSideSearch
